import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerEvidenceQuestion, qaHref } from '../contextLinks';

type LifecycleStage = 'lead' | 'qualified' | 'customer' | 'past_customer' | 'dead';

interface Contact {
  id: number;
  display_name: string;
  primary_phone: string | null;
  primary_email: string | null;
  company: string | null;
  hubspot_id: string | null;
  odoo_partner_id: number | null;
  lifecycle_stage: LifecycleStage | null;
  contact_properties: Record<string, unknown>;
  is_active: boolean;
  soft_deleted_at: string | null;
  soft_deleted_by: string | null;
  created_source: string | null;
  last_updated_source: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

interface ListResponse {
  contacts: Contact[];
  total: number;
  limit: number;
  offset: number;
}

const LIFECYCLE_OPTIONS: Array<{ value: '' | LifecycleStage; label: string }> = [
  { value: '', label: 'All stages' },
  { value: 'lead', label: 'Lead' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'customer', label: 'Customer' },
  { value: 'past_customer', label: 'Past customer' },
  { value: 'dead', label: 'Dead' },
];

const LIFECYCLE_CHIP: Record<LifecycleStage, string> = {
  lead: 'bg-blue-50 text-blue-900 ring-blue-200',
  qualified: 'bg-indigo-50 text-indigo-900 ring-indigo-200',
  customer: 'bg-green-50 text-green-900 ring-green-200',
  past_customer: 'bg-slate-100 text-slate-700 ring-slate-200',
  dead: 'bg-rose-50 text-rose-900 ring-rose-200',
};

const PAGE_SIZE = 50;

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return 'just now';
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))}mo ago`;
  return `${Math.floor(diff / (365 * day))}y ago`;
}

function LifecycleChip({ stage }: { stage: LifecycleStage | null }): JSX.Element {
  if (!stage) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ${LIFECYCLE_CHIP[stage]}`}>
      {stage.replace('_', ' ')}
    </span>
  );
}

export function ContactsList(): JSX.Element {
  const [q, setQ] = useState('');
  const [stage, setStage] = useState<'' | LifecycleStage>('');
  const [showInactive, setShowInactive] = useState(false);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [q, stage, showInactive]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (q.trim().length > 0) params.set('q', q.trim());
    if (stage) params.set('lifecycle_stage', stage);
    if (!showInactive) params.set('is_active', 'true');
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));

    const handle = setTimeout(() => {
      (async () => {
        try {
          const res = await fetch(`/api/contacts?${params.toString()}`, {
            credentials: 'include',
          });
          if (cancelled) return;
          if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { error?: string };
            setError(body.error ?? `Request failed: ${res.status}`);
            setLoading(false);
            return;
          }
          const payload = (await res.json()) as ListResponse;
          setData(payload);
          setLoading(false);
        } catch (err) {
          if (cancelled) return;
          setError((err as Error).message);
          setLoading(false);
        }
      })();
    }, 200); // debounce text search

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q, stage, showInactive, offset]);

  const total = data?.total ?? 0;
  const contacts = data?.contacts ?? [];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Customer evidence</h1>
        <Link
          to="/contacts/new"
          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 transition"
        >
          + Create contact
        </Link>
      </div>
      <p className="text-xs leading-5 text-slate-500">
        Evidence records that join calls, SMS, and engagements to a person. The customer master of
        record is <span className="font-medium text-slate-600">Odoo</span> — creating or editing here
        repairs Sales-QA evidence, not the commercial record.
      </p>

      <section className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            placeholder="Search name, email, phone, company…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 min-w-[16rem] rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as '' | LifecycleStage)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            {LIFECYCLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
            />
            Show inactive
          </label>
        </div>
      </section>

      {error && (
        <section className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-4">
          <p className="text-sm font-medium text-rose-900">Failed to load contacts</p>
          <p className="mt-1 text-sm text-rose-800">{error}</p>
        </section>
      )}

      <section className="rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : contacts.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-700">No contacts found.</p>
            <p className="mt-1 text-xs text-slate-500">Try changing your filters or create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Company</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Phone</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Stage</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Last seen</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contacts.map((c) => (
                  <tr key={c.id} className={c.is_active ? '' : 'bg-slate-50/60'}>
                    <td className="px-4 py-2 text-sm">
                      <Link to={`/contacts/${c.id}`} className="text-slate-900 hover:underline font-medium">
                        {c.display_name}
                      </Link>
                      {!c.is_active && (
                        <span className="ml-2 inline-flex items-center rounded-md bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                          inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-700">{c.company ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-sm text-slate-700">{c.primary_email ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-sm text-slate-700">{c.primary_phone ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-sm"><LifecycleChip stage={c.lifecycle_stage} /></td>
                    <td className="px-4 py-2 text-sm text-slate-500" title={c.last_seen_at}>
                      {relativeTime(c.last_seen_at)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/contacts/${c.id}`}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Context
                        </Link>
                        <Link
                          to={qaHref(customerEvidenceQuestion({
                            name: c.display_name,
                            company: c.company,
                            phone: c.primary_phone,
                            email: c.primary_email,
                            contactId: c.id,
                          }))}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                        >
                          Ask evidence
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <p>
          {total === 0 ? '0 results' : `${pageStart}–${pageEnd} of ${total}`}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
